import React from 'react';
import { CardTW } from '../../../../tailwindComponents/CardTW/CardTW';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { Show } from '../../../shared/Show';
import { FlexRow } from '../../../../ui-toolkit';
import { usePatchAttributesMutation } from '../../../../API/order/attributes/AttributesApi';
import { UUID } from '../../../../types/shared/UUID';
import { IOrderAttributesData } from '../../../../types/order/attributes/IOrderAttributesData';
import { ButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { SimpleGrid } from '../../../../tailwindComponents/grid/SimpleGrid';
import { LabelTextTW } from '../../../../tailwindComponents/formCellTW/LabelTextTW';
import { ErrorMessageTW } from '../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { SpinnerTW } from '../../../../tailwindComponents/spinnerTW/SpinnerTW';
import { OutsideClickHandler } from '../../../shared/InteractionManagement/OutsideClickHandler';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { IBrokerageAgentsData } from '../../../../types/order/attributes/IBrokerageAgentsData';
import { Form, Formik } from 'formik';
import { TextFieldTW } from '../../../../tailwindComponents/textInputTW/TextFieldTW';
import { updateBrokerageAgentsScheme } from '../../../../validation/order/OrderAttributesValidator';
import { SelectFieldTW } from '../../../../tailwindComponents/selectTW/SelectFieldTW';
import { StatesLabelValue } from '../../../../ui-toolkit/enums/states';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { notEmpty } from '../../../../ui-toolkit/utilities/notEmpty';

export interface IBrokerageAgentsSectionProps {
  orderId: UUID;
  attributes?: IOrderAttributesData;
  isCollapsed: boolean;
  setIsCollapsed: (newValue: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (newValue: boolean) => void;
}

const getInitialBrokerageAgentValues = (agent?: IBrokerageAgentsData): IBrokerageAgentsData => {
  return {
    firmName: agent?.firmName,
    address: {
      streetAddress: agent?.address?.streetAddress,
      unit: agent?.address?.unit,
      city: agent?.address?.city,
      stateAbbreviation: agent?.address?.stateAbbreviation,
      zipCode: agent?.address?.zipCode,
    },
  };
};
const convertEmptyStringToUndefined = (field?: string): string | undefined => field || undefined;
const getChangedValue = (initialValue?: string, newValue?: string): string | undefined =>
  convertEmptyStringToUndefined(initialValue) !== convertEmptyStringToUndefined(newValue) ? newValue : undefined;
const getChangedBrokerageAgentsValues = (
  initialValues?: IBrokerageAgentsData,
  newValues?: IBrokerageAgentsData
): IBrokerageAgentsData => {
  const body = { firmName: getChangedValue(initialValues?.firmName, newValues?.firmName) } as IBrokerageAgentsData;
  const address = {
    streetAddress: getChangedValue(initialValues?.address?.streetAddress, newValues?.address?.streetAddress),
    unit: getChangedValue(initialValues?.address?.unit, newValues?.address?.unit),
    city: getChangedValue(initialValues?.address?.city, newValues?.address?.city),
    stateAbbreviation: getChangedValue(
      initialValues?.address?.stateAbbreviation,
      newValues?.address?.stateAbbreviation
    ),
    zipCode: getChangedValue(initialValues?.address?.zipCode, newValues?.address?.zipCode),
  };
  if (Object.values(address).some((value) => notEmpty(value))) {
    body.address = address;
  }

  return body;
};
const convertBrokerageAgentsToPatchData = (
  initialValues?: IOrderAttributesData,
  newValues?: IOrderAttributesData
): IOrderAttributesData => {
  const body = {} as IOrderAttributesData;
  const buyerAgent = getChangedBrokerageAgentsValues(initialValues?.buyerAgent, newValues?.buyerAgent);
  if (Object.values(buyerAgent).some((value) => notEmpty(value))) {
    body.buyerAgent = buyerAgent;
  }
  const sellerAgent = getChangedBrokerageAgentsValues(initialValues?.sellerAgent, newValues?.sellerAgent);
  if (Object.values(sellerAgent).some((value) => notEmpty(value))) {
    body.sellerAgent = sellerAgent;
  }
  return body;
};
const hasBeenUpdated = (agent?: IBrokerageAgentsData): boolean => {
  return (
    getDateString(DateFormat.short, agent?.createdAt) !== getDateString(DateFormat.short, agent?.updatedAt) ||
    getDateString(DateFormat.hugeTime, agent?.createdAt) !== getDateString(DateFormat.hugeTime, agent?.updatedAt)
  );
};

export const BrokerageAgentsSection: React.FC<IBrokerageAgentsSectionProps> = ({
  orderId,
  attributes,
  isCollapsed,
  setIsCollapsed,
  isEditMode,
  setIsEditMode,
}) => {
  const [patchAttributes, { error, isLoading }] = usePatchAttributesMutation();

  const toggleEditMode = (isEditMode: boolean) => setIsEditMode(isEditMode);

  const initialValues = {
    buyerAgent: getInitialBrokerageAgentValues(attributes?.buyerAgent),
    sellerAgent: getInitialBrokerageAgentValues(attributes?.sellerAgent),
    isBuyerAgentAddressRequired: !!attributes?.buyerAgent?.address?.streetAddress,
    isSellerAgentAddressRequired: !!attributes?.sellerAgent?.address?.streetAddress,
  };

  const saveUpdateAttributes = async (values: IOrderAttributesData) => {
    const result = await patchAttributes({ orderId, body: convertBrokerageAgentsToPatchData(initialValues, values) });
    if (checkForMutationSuccess(result, 'Brokerage Information Updated', 'Error Updating Brokerage Information')) {
      toggleEditMode(false);
    }
  };

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={saveUpdateAttributes}
        validationSchema={updateBrokerageAgentsScheme}
        validateOnChange
        enableReinitialize
      >
        {({ submitForm, resetForm }) => (
          <Form>
            <OutsideClickHandler
              enabled={isEditMode}
              onCloseAndSave={() => submitForm()}
              onClose={() => toggleEditMode(false)}
            >
              <div className={'mb-4'}>
                <CardTW
                  title={
                    <div className={'flex flex-row items-center'}>
                      <Show when={isEditMode}>
                        <ChevronDownIcon className="h-7 w-7 flex-none ease-in-out" />
                      </Show>
                      Brokerage Information
                    </div>
                  }
                  titleActions={
                    <Show when={!isLoading} fallback={<SpinnerTW />}>
                      <Show when={!isCollapsed && !isEditMode}>
                        <ButtonTW
                          ghost
                          secondary
                          className="!-ml-6"
                          data-test-id="brokerage-agents-edit-mode-button"
                          onClick={() => toggleEditMode(true)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </ButtonTW>
                      </Show>
                      <Show when={!isCollapsed && isEditMode}>
                        <div className="flex items-center">
                          <ButtonTW type="submit" secondary>
                            Save
                          </ButtonTW>
                          <ButtonTW
                            type="button"
                            ghost
                            secondary
                            className="px-2 font-normal text-foundation-primary hover:underline"
                            onClick={() => {
                              resetForm();
                              toggleEditMode(false);
                            }}
                          >
                            Cancel
                          </ButtonTW>
                        </div>
                      </Show>
                    </Show>
                  }
                  isCollapsible={!isEditMode}
                  isCollapsed={isCollapsed}
                  onChangeCollapseState={() => setIsCollapsed(!isCollapsed)}
                  titleClassName={'text-xl'}
                  key={`brokerage-agents-card-${isCollapsed}`}
                >
                  <SimpleGrid columnCount={2} baseline>
                    <Show
                      when={isEditMode}
                      fallback={
                        <>
                          <div>
                            <SimpleGrid baseline>
                              <div className={'pb-4 text-base font-medium'}>Buyer Agent Brokerage</div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                              <div>
                                <FlexRow className="font-medium">Firm Name</FlexRow>
                                <LabelTextTW labelText={attributes?.buyerAgent?.firmName ?? ''} />
                              </div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                              <div>
                                <FlexRow className="font-medium">Street Address</FlexRow>
                                <LabelTextTW labelText={attributes?.buyerAgent?.address?.streetAddress ?? ''} />
                              </div>
                              <div>
                                <FlexRow className="font-medium">Unit</FlexRow>
                                <LabelTextTW labelText={attributes?.buyerAgent?.address?.unit ?? ''} />
                              </div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} baseline>
                              <div>
                                <FlexRow className="font-medium">City</FlexRow>
                                <LabelTextTW labelText={attributes?.buyerAgent?.address?.city ?? ''} />
                              </div>
                              <div>
                                <SimpleGrid columnCount={2}>
                                  <div>
                                    <FlexRow className="font-medium">State</FlexRow>
                                    <LabelTextTW labelText={attributes?.buyerAgent?.address?.stateAbbreviation ?? ''} />
                                  </div>
                                  <div>
                                    <FlexRow className="font-medium">Zip Code</FlexRow>
                                    <LabelTextTW labelText={attributes?.buyerAgent?.address?.zipCode ?? ''} />
                                  </div>
                                </SimpleGrid>
                              </div>
                              <Show when={hasBeenUpdated(attributes?.buyerAgent)}>
                                <FlexRow className="text-sm text-gray-500">
                                  Last updated on{' '}
                                  {getDateString(DateFormat.shortDateTimeAt, `${attributes?.buyerAgent?.updatedAt}`)}
                                </FlexRow>
                              </Show>
                            </SimpleGrid>
                          </div>
                          <div>
                            <SimpleGrid baseline>
                              <div className={'pb-4 text-base font-medium'}>Seller Agent Brokerage</div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                              <div>
                                <FlexRow className="font-medium">Firm Name</FlexRow>
                                <LabelTextTW labelText={attributes?.sellerAgent?.firmName ?? ''} />
                              </div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                              <div>
                                <FlexRow className="font-medium">Street Address</FlexRow>
                                <LabelTextTW labelText={attributes?.sellerAgent?.address?.streetAddress ?? ''} />
                              </div>
                              <div>
                                <FlexRow className="font-medium">Unit</FlexRow>
                                <LabelTextTW labelText={attributes?.sellerAgent?.address?.unit ?? ''} />
                              </div>
                            </SimpleGrid>
                            <SimpleGrid columnCount={2} baseline>
                              <div>
                                <FlexRow className="font-medium">City</FlexRow>
                                <LabelTextTW labelText={attributes?.sellerAgent?.address?.city ?? ''} />
                              </div>
                              <div>
                                <SimpleGrid columnCount={2}>
                                  <div>
                                    <FlexRow className="font-medium">State</FlexRow>
                                    <LabelTextTW
                                      labelText={attributes?.sellerAgent?.address?.stateAbbreviation ?? ''}
                                    />
                                  </div>
                                  <div>
                                    <FlexRow className="font-medium">Zip Code</FlexRow>
                                    <LabelTextTW labelText={attributes?.sellerAgent?.address?.zipCode ?? ''} />
                                  </div>
                                </SimpleGrid>
                              </div>
                              <Show when={hasBeenUpdated(attributes?.sellerAgent)}>
                                <FlexRow className="text-sm text-gray-500">
                                  Last updated on{' '}
                                  {getDateString(DateFormat.shortDateTimeAt, `${attributes?.sellerAgent?.updatedAt}`)}
                                </FlexRow>
                              </Show>
                            </SimpleGrid>
                          </div>
                        </>
                      }
                    >
                      <div>
                        <SimpleGrid baseline>
                          <div className={'pb-4 text-base font-medium'}>Buyer Agent Brokerage</div>
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                          <TextFieldTW name="buyerAgent.firmName" labelText={'Firm Name'} />
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                          <TextFieldTW
                            name="buyerAgent.address.streetAddress"
                            labelText={'Street Address'}
                            isRequired={initialValues.isBuyerAgentAddressRequired}
                          />
                          <TextFieldTW name="buyerAgent.address.unit" labelText={'Unit'} />
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} baseline>
                          <TextFieldTW
                            name="buyerAgent.address.city"
                            labelText={'City'}
                            isRequired={initialValues.isBuyerAgentAddressRequired}
                          />
                          <SimpleGrid columnCount={2} baseline>
                            <div>
                              <LabelTextTW labelText={'State'} isRequired={initialValues.isBuyerAgentAddressRequired} />
                              <SelectFieldTW
                                name="buyerAgent.address.stateAbbreviation"
                                options={StatesLabelValue}
                                className={'-mt-1 '}
                                isMenuPlacementTop
                                isRequired={initialValues.isBuyerAgentAddressRequired}
                              />
                            </div>
                            <TextFieldTW
                              name="buyerAgent.address.zipCode"
                              labelText={'Zip Code'}
                              isRequired={initialValues.isBuyerAgentAddressRequired}
                            />
                          </SimpleGrid>
                        </SimpleGrid>
                      </div>
                      <div>
                        <SimpleGrid baseline>
                          <div className={'pb-4 text-base font-medium'}>Seller Agent Brokerage</div>
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                          <TextFieldTW name="sellerAgent.firmName" labelText={'Firm Name'} />
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} className={'mb-4'} baseline>
                          <TextFieldTW
                            name="sellerAgent.address.streetAddress"
                            labelText={'Street Address'}
                            isRequired={initialValues.isSellerAgentAddressRequired}
                          />
                          <TextFieldTW name="sellerAgent.address.unit" labelText={'Unit'} />
                        </SimpleGrid>
                        <SimpleGrid columnCount={2} baseline>
                          <TextFieldTW
                            name="sellerAgent.address.city"
                            labelText={'City'}
                            isRequired={initialValues.isSellerAgentAddressRequired}
                          />
                          <SimpleGrid columnCount={2}>
                            <div>
                              <LabelTextTW
                                labelText={'State'}
                                isRequired={initialValues.isSellerAgentAddressRequired}
                              />
                              <SelectFieldTW
                                name="sellerAgent.address.stateAbbreviation"
                                options={StatesLabelValue}
                                className={'-mt-1 '}
                                isMenuPlacementTop
                                isRequired={initialValues.isSellerAgentAddressRequired}
                              />
                            </div>
                            <TextFieldTW
                              name="sellerAgent.address.zipCode"
                              labelText={'Zip Code'}
                              isRequired={initialValues.isSellerAgentAddressRequired}
                            />
                          </SimpleGrid>
                        </SimpleGrid>
                      </div>
                    </Show>
                  </SimpleGrid>
                </CardTW>
              </div>
            </OutsideClickHandler>
          </Form>
        )}
      </Formik>
    </>
  );
};
